import { COPILOT_MAIN_AGENT_LABEL } from '@terra/shared-dto-chat-types';
import type { ProcessedPart } from '../utils/message-processing';

const CODE_ANALYST_STREAM_ORIGIN = 'subagent::code-analyst';

export function getPartTerraStreamOrigins(
  processedPart: ProcessedPart,
): string[] {
  const providerMetadata = (
    processedPart.part as
      | {
          providerMetadata?: {
            metadata?: unknown;
          };
        }
      | undefined
  )?.providerMetadata;

  const metadata = providerMetadata?.metadata;

  if (!metadata || typeof metadata !== 'object') {
    return [];
  }

  const metadataRecord = metadata as Record<string, unknown>;
  const origins: string[] = [];

  if (typeof metadataRecord.terraStreamOrigin === 'string') {
    origins.push(metadataRecord.terraStreamOrigin);
  }

  if (Array.isArray(metadataRecord.tags)) {
    origins.push(
      ...metadataRecord.tags.filter(
        (tag): tag is string => typeof tag === 'string',
      ),
    );
  }

  return origins;
}

function normalizeOriginToken(value: string): string {
  return value.replace(/_/g, '-').toLowerCase();
}

function isCodeAnalystWriteupPart(processedPart: ProcessedPart): boolean {
  if (processedPart.kind !== 'assistant-text') {
    return false;
  }

  if (processedPart.part.type === 'reasoning') {
    return false;
  }

  return getPartTerraStreamOrigins(processedPart).some(
    (origin) => origin === CODE_ANALYST_STREAM_ORIGIN,
  );
}

export function isCopilotMainAgentOrigin(
  processedPart: ProcessedPart,
): boolean {
  return getPartTerraStreamOrigins(processedPart).some(
    (origin) => origin === COPILOT_MAIN_AGENT_LABEL,
  );
}

export function isCopilotMainAgentAssistantText(
  processedPart: ProcessedPart,
): boolean {
  if (processedPart.kind !== 'assistant-text') {
    return false;
  }

  if (processedPart.part.type === 'reasoning') {
    return false;
  }

  return isCopilotMainAgentOrigin(processedPart);
}

export function isSubAgentAssistantPart(processedPart: ProcessedPart): boolean {
  if (processedPart.kind !== 'assistant-text') {
    return false;
  }

  const origins = getPartTerraStreamOrigins(processedPart);

  if (origins.some((origin) => origin === COPILOT_MAIN_AGENT_LABEL)) {
    return false;
  }

  if (isCodeAnalystWriteupPart(processedPart)) {
    return false;
  }

  return true;
}

export function assistantTextMatchesOpenGroup({
  processedPart,
  parentToolName,
}: {
  processedPart: ProcessedPart;
  parentToolName?: string;
}): boolean {
  if (isCopilotMainAgentOrigin(processedPart)) {
    return false;
  }

  const origins = getPartTerraStreamOrigins(processedPart);

  if (origins.length === 0 || !parentToolName) {
    return true;
  }

  const parent = normalizeOriginToken(parentToolName);

  return origins.some((origin) => {
    const normalized = normalizeOriginToken(origin);

    return (
      normalized === `subagent::${parent}` || normalized.endsWith(`::${parent}`)
    );
  });
}

export function getLastCodeAnalystWriteupKey(
  parts: ProcessedPart[],
): string | undefined {
  return parts.reduce<string | undefined>((lastKey, part) => {
    if (isCodeAnalystWriteupPart(part)) {
      return part.key;
    }

    return lastKey;
  }, undefined);
}

export function getLastCodeAnalystWriteupKeyByParent({
  parts,
  isCodeAnalystParent,
  isOtherSubAgentParent,
}: {
  parts: ProcessedPart[];
  isCodeAnalystParent: (part: ProcessedPart) => boolean;
  isOtherSubAgentParent: (part: ProcessedPart) => boolean;
}): Map<string, string> {
  const lastByParent = new Map<string, string>();
  let currentParent: string | undefined;

  for (const part of parts) {
    if (isCodeAnalystParent(part)) {
      currentParent = part.key;
      continue;
    }

    if (isOtherSubAgentParent(part)) {
      currentParent = undefined;
      continue;
    }

    if (currentParent && isCodeAnalystWriteupPart(part)) {
      lastByParent.set(currentParent, part.key);
    }
  }

  return lastByParent;
}

export function shouldNestSubAgentAssistantPart({
  processedPart,
  lastCodeAnalystWriteupKey,
  lastCodeAnalystWriteupKeys,
  liftLastWriteup = true,
}: {
  processedPart: ProcessedPart;
  lastCodeAnalystWriteupKey?: string;
  lastCodeAnalystWriteupKeys?: ReadonlySet<string>;
  liftLastWriteup?: boolean;
}): boolean {
  if (isSubAgentAssistantPart(processedPart)) {
    return true;
  }

  if (!isCodeAnalystWriteupPart(processedPart)) {
    return false;
  }

  if (!liftLastWriteup) {
    return true;
  }

  if (lastCodeAnalystWriteupKeys && lastCodeAnalystWriteupKeys.size > 0) {
    return !lastCodeAnalystWriteupKeys.has(processedPart.key);
  }

  return processedPart.key !== lastCodeAnalystWriteupKey;
}

export type AssistantTextPlacement = 'nest' | 'main-agent' | 'lift';

export function resolveAssistantTextPlacement({
  processedPart,
  lastCodeAnalystWriteupKey,
  lastCodeAnalystWriteupKeys,
  hasOpenSubAgent,
  groupIsRunning,
  originMatchesOpenGroup = true,
}: {
  processedPart: ProcessedPart;
  lastCodeAnalystWriteupKey?: string;
  lastCodeAnalystWriteupKeys?: ReadonlySet<string>;
  hasOpenSubAgent: boolean;
  groupIsRunning: boolean;
  originMatchesOpenGroup?: boolean;
}): AssistantTextPlacement {
  const isReasoning =
    processedPart.kind === 'assistant-text' &&
    processedPart.part.type === 'reasoning';
  const nestableInGroup =
    isReasoning ||
    shouldNestSubAgentAssistantPart({
      processedPart,
      lastCodeAnalystWriteupKey,
      lastCodeAnalystWriteupKeys,
      liftLastWriteup: !groupIsRunning,
    });

  if (hasOpenSubAgent && originMatchesOpenGroup && nestableInGroup) {
    return 'nest';
  }

  if (isCopilotMainAgentOrigin(processedPart)) {
    return 'main-agent';
  }

  return 'lift';
}
